<template>
    <v-card class="job-card" :id="'card-'+job.id" :class="{'moveable-card':(!job.archived && ($windowWidth > 960 || !smartLock) && !job.activeTask.running), 'locked-job-card':job.archived }">
        <div v-if="job.archived" class="locked-card">
            <v-icon icon="mdi-lock" class="mr-1" size="x-small"></v-icon> LOCKED
        </div>
        <v-card-title>
            <span v-html="formatSearchName(job.name, searchTerm)"></span>
        </v-card-title>
        <v-card-subtitle>
            <contact-link :contact="job.contact" :search-term="searchTerm"></contact-link>
        </v-card-subtitle>
        <v-card-text>
            <v-badge color="grey-lighten-4"
                     v-if="job.estimatedCoatingTime && job.estimatedCoatingTime > 0"
                     class="time-badge">
                <template #badge>
                    <v-icon icon="mdi-timer-outline" size="x-small"></v-icon>
                    <span class="time-badge-text">{{ convertMinutesToHoursMinutes(job.estimatedCoatingTime) }}</span>
                </template>
            </v-badge>
            <div v-if="job.customerNumber">
                <small class="text-grey-darken-2"><v-icon icon="mdi-pound-box-outline"></v-icon></small> <span v-html="formatSearchName(job.customerNumber, searchTerm)"></span>
            </div>
            <div v-if="job.dateTargetCompletion">
                <small class="text-grey-darken-2"><v-icon icon="mdi-bullseye"></v-icon></small><span v-if="job.dateReceiveOriginal">{{ $dayjs(job.dateReceiveOriginal).format("DD/MM") }} -</span> {{ $dayjs(job.dateTargetCompletion).format("DD/MM") }} <v-chip v-if="$dayjs(job.dateTargetCompletion).isBefore($dayjs(), 'day') && !job.archived" variant="flat" size="x-small" class="ml-1" color="red-darken-2">OVERDUE</v-chip>
            </div>
            <div v-if="job.dateReceiveEstimate">
                <small class="text-grey-darken-2"><v-icon icon="mdi-truck-delivery-outline"></v-icon></small> {{ $dayjs(job.dateReceiveEstimate).format("DD/MM") }} <v-chip v-if="!job.dateReceived && $dayjs(job.dateReceiveEstimate).isBefore($dayjs(), 'day')" variant="flat" size="x-small" class="ml-1" color="red-darken-2">OVERDUE</v-chip>
            </div>
            <div v-if="job.colours">
                <small class="text-grey-darken-2"><v-icon icon="mdi-palette"></v-icon></small> <span v-html="formatSearchName(job.colours.replace('Duralloy ', ''), searchTerm)"></span>
            </div>
            <div v-if="job.activeTask && job.activeTask.assignedLocation">
                <small class="text-grey-darken-2"><v-icon icon="mdi-map-marker-radius"></v-icon></small> {{ job.activeTask.assignedLocation }}
            </div>
            <div class="d-flex justify-space-between">
                <div>
                    <div v-if="job.taskSubTotal != 0"><small class="text-grey-darken-2"><v-icon icon="mdi-invoice-text-outline"></v-icon></small> ${{ numberWithCommas(job.taskSubTotal, 2) }}</div>
                    <div v-if="job.reworkTotal && job.taskSubTotal == 0"><small class="text-grey-darken-2"><v-icon icon="mdi-glass-fragile"></v-icon></small> ${{ numberWithCommas(job.reworkTotal, 2) }}</div>
                </div>
                <v-tooltip text="View Job" class="float-right">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" icon="mdi-briefcase-eye" variant="plain" size="x-small" @click="openSmartJobDialog(job)"></v-btn>
                    </template>
                </v-tooltip>
            </div>
        </v-card-text>
        <div v-if="job.activeTask && (job.activeTask.status == $constants.jobs.JOBSTAT_LINE1 || job.activeTask.status == $constants.jobs.JOBSTAT_LINE2) && job.activeTask.running" class="spraying-card">
            <v-progress-circular :size="10"
                                 :width="5"
                                 color="orange"
                                 indeterminate></v-progress-circular> SPRAYING <job-task-timer :task="job.activeTask"></job-task-timer>
        </div>
        <div v-if="job.activeTask && (job.activeTask.status == $constants.jobs.JOBSTAT_LINE1 || job.activeTask.status == $constants.jobs.JOBSTAT_LINE2) && job.activeTask.paused" class="spraying-paused-card">
            <v-icon icon="mdi-pause-box-outline" size="x-small"></v-icon> SPRAYING PAUSED
        </div>
        <div v-if="job.activeTask && job.activeTask.status == $constants.jobs.JOBSTAT_STRIPPING && job.activeTask.running" class="stripping-card">
            <v-progress-circular :size="10"
                                 :width="5"
                                 color="yellow"
                                 indeterminate></v-progress-circular> STRIPPING <job-task-timer :task="job.activeTask"></job-task-timer>
        </div>
        <div v-if="job.activeTask && job.activeTask.status == $constants.jobs.JOBSTAT_BLASTING && job.activeTask.running" class="stripping-card">
            <v-progress-circular :size="10"
                                 :width="5"
                                 color="yellow"
                                 indeterminate></v-progress-circular> BLASTING <job-task-timer :task="job.activeTask"></job-task-timer>
        </div>
        <div v-if="job.jobStatus <= $constants.jobs.JOBSTAT_INVOICE && (job.activeTask.status == $constants.jobs.JOBSTAT_LINE1 || job.activeTask.status == $constants.jobs.JOBSTAT_LINE2) && job.activeTask.completed && !job.activeTask.packed" class="spray-complete-card">
            <v-icon icon="mdi-marker-check" size="x-small"></v-icon> SPRAYING COMPLETE
        </div>
        <div v-if="job.jobStatus <= $constants.jobs.JOBSTAT_INVOICE && (job.activeTask.status == $constants.jobs.JOBSTAT_LINE1 || job.activeTask.status == $constants.jobs.JOBSTAT_LINE2) && job.activeTask.completed && job.activeTask.packed" class="packing-complete-card">
            <v-icon icon="mdi-package-variant-closed-check" size="x-small"></v-icon> PACKING COMPLETE
        </div>
        <div v-if="(!job.dateReceived || job.dateReceived.length == 0) && job.jobStatus < $constants.jobs.JOBSTAT_INVOICE" class="received-card">
            <v-icon icon="mdi-map-marker-off" size="x-small"></v-icon> NOT RECEIVED
        </div>
        <div v-if="job.datePretreatment && job.jobStatus < $constants.jobs.JOBSTAT_INVOICE" class="pretreated-card">
            <v-icon icon="mdi-progress-check" size="x-small"></v-icon> PRETREATED
        </div>
        <div v-if="job.dateBlasted && job.jobStatus < $constants.jobs.JOBSTAT_INVOICE" class="blasted-card">
            <v-icon icon="mdi-progress-check" size="x-small"></v-icon> BLASTED
        </div>
        <div v-if="job.dateStripped && job.jobStatus < $constants.jobs.JOBSTAT_INVOICE" class="stripped-card">
            <v-icon icon="mdi-progress-check" size="x-small"></v-icon> STRIPPED
        </div>
        <div v-if="job.taskJobTag == $constants.jobs.TAG_REWORK" class="rework-card">
            <v-icon icon="mdi-recycle" size="x-small"></v-icon> REWORK
        </div>
        <div v-if="job.jobTag == $constants.jobs.TAG_BATCH" class="batch-card">
            <v-icon icon="mdi-toaster-oven" size="x-small"></v-icon> BATCH OVEN
        </div>
        <div v-if="job.jobTag == $constants.jobs.TAG_HIGH && job.jobStatus < $constants.jobs.JOBSTAT_INVOICE" class="priority-card">
            <v-icon icon="mdi-flag" size="x-small"></v-icon> HIGH PRIORITY
        </div>
        <div v-if="job.jobStatus == $constants.jobs.JOBSTAT_COMP" class="invoice-card">
            <v-icon icon="mdi-progress-check" size="x-small"></v-icon> INVOICED
        </div>
        <div v-if="job.jobStatus == $constants.jobs.JOBSTAT_INVOICE" class="completed-card">
            <v-icon icon="mdi-progress-check" size="x-small"></v-icon> COMPLETED
        </div>
    </v-card>
</template>
<style scoped lang="scss">
    @import '../../../styles/smart.scss';
    .time-badge {
        position: absolute; 
        top: 10px;
        right: 40px;
    }
    .locked-job-card .time-badge {
        top:30px;
    }
    .time-badge-text {
        font-size: 10px !important;
        margin-left: 2px;
    }
</style>
<script>
    import { mapActions, mapState } from "vuex";
    import { formatSearchName } from "../../../mixins/searchMixins";
    import { numberWithCommas, convertMinutesToHoursMinutes } from "../../../mixins/numberMixins";
    function initialState() {
        return {
            saving: false,
            loading: false,
        };
    }
    export default {
        name: "SmartCard",
        data: function () {
            return initialState();
        },
        props: {
            job: Object,
            searchTerm: String
        },
        methods: {
            ...mapActions([
                "showErrorSnack",
                "showSuccessSnack",
                "openSmartJobDialog",
            ]),
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            formatSearchName,
            numberWithCommas,
            convertMinutesToHoursMinutes
        },
        computed: {
            ...mapState({
                smartLock: (state) => state.smart.smartLock,
            }),
        }
    };
</script>